import React from 'react';
import { getBoxSlotStyle, resolveRenderableBox } from '../../../utils/privateLayouts';
import { Group, Rect, Text } from 'react-konva';
import { getInterSlotGap } from '../../../utils/presetLayout';
import {
  describeLayoutItem,
  getPresetVisibleSlotLanguages,
  logLayoutDebug,
  resolveBackgroundMode,
} from '../model/presetLayoutHelpers';

export default function PageTextItemNode({
  item: savedItem,
  languages,
  presets = [],
  bookDefaultPresetId,
  canvasSize,
  onSelect,
  onMove,
  onMoveEnd,
}) {
  const resolved = resolveRenderableBox(savedItem, presets, bookDefaultPresetId, canvasSize, languages);
  const item = resolved.box;
  const preset = resolved.preset;
  const dragStartPosition = React.useRef(null);
  const position = item.position || {};
  const x = Number(position.x) || 0;
  const y = Number(position.y) || 0;
  const width = Number(position.width) || 260;
  const height = Number(position.height) || 100;
  const padding = Number(item.style?.padding ?? preset?.config?.padding?.left ?? 24);
  // A canvas placement can be reused while the editor switches language pairs.
  // The active flags are the source of truth for which translations to render;
  // item.slotLanguages only records the pair that was active when this item was
  // created and must not cause a previous pair to remain on screen.
  const slotLanguages = Array.isArray(languages) && languages.length > 0
    ? languages
    : Array.isArray(item.slotLanguages) && item.slotLanguages.length > 0
      ? item.slotLanguages
      : ['en'];
  const visibleSlotLanguages = getPresetVisibleSlotLanguages(item, slotLanguages);
  const slotCount = Math.max(visibleSlotLanguages.length, 1);
  const innerWidth = Math.max(20, width - padding * 2);
  const innerHeight = Math.max(30, height - padding * 2);
  const effectiveShowDivider = Boolean(item.style?.showDivider ?? preset?.config?.showDivider);
  const separatorHeight = effectiveShowDivider
    ? Number(item.style?.separatorHeight || preset?.config?.separatorHeight || 6)
    : 0;
  const separatorPadding = Number(item.style?.separatorPadding ?? preset?.config?.separatorPadding ?? 0);
  const interSlotGap = getInterSlotGap({
    separatorPadding,
    separatorHeight,
    showDivider: effectiveShowDivider,
  });
  const availableHeight = Math.max(
    30,
    innerHeight - (slotCount > 1 ? interSlotGap * (slotCount - 1) : 0),
  );
  const slotHeight = Math.max(30, Math.floor(availableHeight / slotCount));
  const alignment = item.style?.alignment || item.align || preset?.config?.alignment || 'center';
  const baseFontSize = Number(item.style?.fontSize || preset?.config?.fontSize || 54);
  const primaryFontFamily = item.style?.fontFamily || preset?.config?.fontFamily || 'Futura';
  const secondaryFontFamily =
    item.style?.fontFamilySecondary || preset?.config?.fontFamilySecondary || primaryFontFamily;
  const primaryFontStyle = item.style?.fontStyle || preset?.config?.fontStyle || 'normal';
  const secondaryFontStyle =
    item.style?.fontStyleSecondary || preset?.config?.fontStyleSecondary || 'italic';
  const lineHeight = Number(item.style?.lineHeight || preset?.config?.lineHeight || 1.05);
  const backgroundMode = resolveBackgroundMode(item.style, preset?.config);
  const showTextBackground = backgroundMode === 'text';
  // Bulk preview generation paints the configured white background inside the
  // text item's bounds. Keep the editable canvas in sync with that output: a
  // page-sized rect here would cover the artwork and every text item rendered
  // beneath this one.
  const showFullBackground = backgroundMode === 'full';

  const measureTextHeight = (text, fontFamily, fontStyle, fontSize = baseFontSize) => {
    if (!item.style?.presetFlow || !window.Konva?.Text) {
      return null;
    }

    const measured = new window.Konva.Text({
      text: text || ' ',
      fontSize,
      fontFamily,
      fontStyle,
      width: innerWidth,
      align: alignment,
      lineHeight,
      wrap: 'word',
    });
    const measuredHeight = measured.height();
    measured.destroy();
    return measuredHeight;
  };

  const measuredSlots = visibleSlotLanguages.map((language) => {
    const originalSlotIndex = Math.max(0, slotLanguages.indexOf(language));
    const resolvedSlot = savedItem.schemaVersion === 1
      ? getBoxSlotStyle(item.style, language, originalSlotIndex)
      : { fontFamily: originalSlotIndex === 0 ? primaryFontFamily : secondaryFontFamily, fontStyle: originalSlotIndex === 0 ? primaryFontStyle : secondaryFontStyle, fontSize: baseFontSize };
    const slotFontFamily = resolvedSlot.fontFamily;
    const slotFontStyle = resolvedSlot.fontStyle;
    const measuredHeight = measureTextHeight(
      item.texts?.[language] || '',
      slotFontFamily,
      slotFontStyle,
      resolvedSlot.fontSize,
    );

    return {
      language,
      fontFamily: slotFontFamily,
      fontStyle: slotFontStyle,
      fontSize: resolvedSlot.fontSize,
      height: measuredHeight,
    };
  });

  const measuredTotalHeight = measuredSlots.reduce(
    (total, slot, slotIndex) =>
      total +
      (slot.height || slotHeight) +
      (slotIndex < measuredSlots.length - 1 ? interSlotGap : 0),
    0,
  );
  const measuredStartY = item.style?.presetFlow
    ? padding + Math.max(0, (innerHeight - measuredTotalHeight) / 2)
    : padding;
  let measuredCursorY = measuredStartY;

  React.useEffect(() => {
    logLayoutDebug('konva-rendered-item', {
      item: describeLayoutItem(item),
      pagePreset: {
        id: preset?._id,
        name: preset?.name,
        config: preset?.config,
      },
      resolved: {
        width,
        height,
        padding,
        innerWidth,
        alignment,
        baseFontSize,
        primaryFontFamily,
        secondaryFontFamily,
        primaryFontStyle,
        secondaryFontStyle,
        lineHeight,
        effectiveShowDivider,
        separatorHeight,
        separatorPadding,
        measuredSlots: measuredSlots.map((slot) => ({
          language: slot.language,
          fontFamily: slot.fontFamily,
          fontStyle: slot.fontStyle,
          height: slot.height,
        })),
      },
    });
  }, [
    alignment,
    baseFontSize,
    effectiveShowDivider,
    height,
    innerWidth,
    item,
    lineHeight,
    measuredSlots,
    padding,
    preset,
    primaryFontFamily,
    primaryFontStyle,
    secondaryFontFamily,
    secondaryFontStyle,
    separatorHeight,
    separatorPadding,
    width,
  ]);

  return (
    <Group
      id={`page-text-item-${item.id}`}
      x={x}
      y={y}
      draggable={Boolean(onMove)}
      onClick={(event) => {
        event.cancelBubble = true;
        onSelect?.(item.id, savedItem);
      }}
      onTap={(event) => {
        event.cancelBubble = true;
        onSelect?.(item.id, savedItem);
      }}
      onDragStart={(event) => {
        event.cancelBubble = true;
        dragStartPosition.current = {
          x: Math.round(event.target.x()),
          y: Math.round(event.target.y()),
          positionMode: item.style?.positionMode,
        };
        onSelect?.(item.id, savedItem);
      }}
      onDragMove={(event) => {
        onMove?.(item.id, {
          width, height,
          x: Math.round(event.target.x()),
          y: Math.round(event.target.y()),
        });
      }}
      onDragEnd={(event) => {
        const position = {
          width, height,
          x: Math.round(event.target.x()),
          y: Math.round(event.target.y()),
        };
        onMove?.(item.id, position);
        if (dragStartPosition.current) onMoveEnd?.(item.id, dragStartPosition.current, position);
        dragStartPosition.current = null;
      }}
    >
      {showFullBackground && (
        <Rect
          width={width}
          height={height}
          fill="white"
          strokeWidth={0}
          listening={false}
        />
      )}
      {showTextBackground && (
        <Rect
          width={width}
          height={height}
          fill="white"
          strokeWidth={0}
        />
      )}
      {measuredSlots.map((slot, slotIndex) => {
        const { language } = slot;
        const text = item.texts?.[language] || '';
        const resolvedSlotHeight = slot.height || slotHeight;
        const slotTop = item.style?.presetFlow
          ? measuredCursorY
          : padding + slotIndex * (slotHeight + interSlotGap);
        if (item.style?.presetFlow) {
          measuredCursorY +=
            resolvedSlotHeight +
            (slotIndex < visibleSlotLanguages.length - 1 ? interSlotGap : 0);
        }
        const isLastSlot = slotIndex === visibleSlotLanguages.length - 1;
        return (
          <React.Fragment key={`${item.id}-${language}-${slotIndex}`}>
            <Text
              text={text || ' '}
              x={padding}
              y={slotTop}
              width={innerWidth}
              height={
                item.style?.presetFlow
                  ? resolvedSlotHeight
                  : isLastSlot ? Math.max(30, height - slotTop - padding) : slotHeight
              }
              fontSize={slot.fontSize || baseFontSize}
              fontFamily={slot.fontFamily}
              fontStyle={slot.fontStyle}
              align={alignment}
              wrap="word"
              lineHeight={lineHeight}
              fill={item.style?.fill || 'black'}
            />
            {effectiveShowDivider && !isLastSlot && (
              <Rect
                x={padding}
                y={slotTop + resolvedSlotHeight + (item.style?.presetFlow ? separatorPadding : 0)}
                width={innerWidth}
                height={separatorHeight}
                fill={item.style?.fill || 'black'}
              />
            )}
          </React.Fragment>
        );
      })}
    </Group>
  );
}
